<svelte:options customElement="hb-messages-list" />

<script lang="ts">
  import parseStyle from "style-to-object";
  import {
    addComponent,
    getChildStyleToPass,
    LanguageTranslator,
  } from "wc-js-utils";
  import type { Component, TAuthor, Events } from "./types/webcomponent.type";

  function dispatch(name: string, detail: any) {
    $host().dispatchEvent(new CustomEvent(name, { detail }));
  }

  // Default options
  const defaultOptions = {
    showTimestamp: true,
    showActions: true,
    showAvatar: true,
    showName: true,
    bubbles: false,
  };

  let {
    id = "",
    // style = "",
    messages,
    authors,
    options = defaultOptions,
    actions = [],
  }: Component = $props();

  type Message = Component["messages"][0] & {
    author: Component["authors"][0];
    dateString: string;
    showAvatar: boolean;
    isConsecutive: boolean;
  };

  // Helper function to get default avatar
  function getDefaultAvatar(isAI: boolean = false): string {
    if (isAI) {
      return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIyMCIgZmlsbD0iIzYzNjZGMCIvPjxwYXRoIGQ9Ik0yMCAxMEMxNS41OCAxMCAxMiAxMy41OCAxMiAxOEMxMiAyMi40MiAxNS41OCAyNiAyMCAyNkMyNC40MiAyNiAyOCAyMi40MiAyOCAxOEMyOCAxMy41OCAyNC40MiAxMCAyMCAxMFpNMjAgMjRDMTYuNjkgMjQgMTQgMjEuMzEgMTQgMThDMTQgMTQuNjkgMTYuNjkgMTIgMjAgMTJDMjMuMzEgMTIgMjYgMTQuNjkgMjYgMThDMjYgMjEuMzEgMjMuMzEgMjQgMjAgMjRaIiBmaWxsPSJ3aGl0ZSIvPjxwYXRoIGQ9Ik0xNiAxNkgxNlYxOEgxNlYxNloiIGZpbGw9IndoaXRlIi8+PHBhdGggZD0iTTI0IDE2SDI0VjE4SDI0VjE2WiIgZmlsbD0id2hpdGUiLz48cGF0aCBkPSJNMTggMjBIMjJWMjJIMThWMjBaIiBmaWxsPSJ3aGl0ZSIvPjwvc3ZnPg==";
    }
    return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIyMCIgZmlsbD0iIzZDNzU3NyIvPjxwYXRoIGQ9Ik0yMCAxMEMxNS41OCAxMCAxMiAxMy41OCAxMiAxOEMxMiAyMi40MiAxNS41OCAyNiAyMCAyNkMyNC40MiAyNiAyOCAyMi40MiAyOCAxOEMyOCAxMy41OCAyNC40MiAxMCAyMCAxMFpNMjAgMjRDMTYuNjkgMjQgMTQgMjEuMzEgMTQgMThDMTQgMTQuNjkgMTYuNjkgMTIgMjAgMTJDMjMuMzEgMTIgMjYgMTQuNjkgMjYgMThDMjYgMjEuMzEgMjMuMzEgMjQgMjAgMjRaIiBmaWxsPSJ3aGl0ZSIvPjxwYXRoIGQ9Ik0xNiAxNkgxNlYxOEgxNlYxNloiIGZpbGw9IndoaXRlIi8+PHBhdGggZD0iTTI0IDE2SDI0VjE4SDI0VjE2WiIgZmlsbD0id2hpdGUiLz48cGF0aCBkPSJNMTggMjBIMjJWMjJIMThWMjBaIiBmaWxsPSJ3aGl0ZSIvPjwvc3ZnPg==";
  }

  // Helper function to copy text to clipboard
  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      // You could add a toast notification here
      console.log("Message copied to clipboard");
    });
  }

  let msgs: Message[] = $derived.by(() => {
    if (!messages || !authors) return [];

    const processedMessages =
      messages
        ?.filter?.(
          (f) =>
            authors?.find?.((a) => a?.id && f?.authorId && a.id === f.authorId)
              ?.id,
        )
        ?.map?.((message, index) => {
          const author = authors?.find?.(
            (a) => a.id === message.authorId,
          ) as TAuthor;
          const isToday =
            new Date(message.timestamp).toDateString() ===
            new Date().toDateString();
          const messageTimestamp = new Date(message.timestamp);
          const hourAndMinutes =
            messageTimestamp.toLocaleTimeString().split(":")[0] +
            ":" +
            messageTimestamp.toLocaleTimeString().split(":")[1];
          const dateAndHourAndMinutes =
            messageTimestamp.toLocaleString().split(" ")[0] +
            " " +
            messageTimestamp.toLocaleTimeString().split(":")[0] +
            ":" +
            messageTimestamp.toLocaleTimeString().split(":")[1];
          const dateString = isToday ? hourAndMinutes : dateAndHourAndMinutes;

          // Check if this is a consecutive message from the same author
          const previousMessage = index > 0 ? messages[index - 1] : null;
          const isConsecutive = Boolean(
            previousMessage &&
              previousMessage.authorId === message.authorId &&
              new Date(message.timestamp).getTime() -
                new Date(previousMessage.timestamp).getTime() <
                300000, // 5 minutes
          );

          // Show avatar if not consecutive or if it's the first message, and if showAvatar option is enabled
          const showAvatar =
            options.showAvatar && (!isConsecutive || index === 0)
              ? true
              : false;

          return {
            ...message,
            author,
            dateString,
            showAvatar,
            isConsecutive,
          };
        }) || [];

    return processedMessages;
  });

  let parsedStyle: { [x: string]: string } = $state({});
  //  let componentStyleToSet: string = "";

  $effect(() => {
    if (typeof messages === "string") {
      try {
        messages = JSON.parse(messages);
      } catch (err) {
        console.error("error parsing messages", err);
      }
    }
    if (typeof actions === "string") {
      try {
        actions = JSON.parse(actions);
      } catch (err) {
        console.error("error parsing actions", err);
      }
    }
  });
  $effect(() => {
    if (typeof options === "string") {
      try {
        options = JSON.parse(options);
        // Merge with defaults
        options = { ...defaultOptions, ...options };
      } catch (err) {
        console.error("error parsing options", err);
      }
    }
  });
  $effect(() => {
    if (typeof authors === "string") {
      try {
        authors = JSON.parse(authors);
      } catch (err) {
        console.error("error parsing authors", err);
      }
    }
  });
</script>

<!-- Bootstrap Icons CSS -->
<svelte:head>
  <link
    rel="stylesheet"
    href="https://cdn.jsdelivr.net/npm/bootstrap-icons@latest/font/bootstrap-icons.css"
  />
</svelte:head>

{#each msgs as message (message.id)}
  <!-- Author name positioned above message content -->
  {#if options.showName}
    <div
      class="author-name-wrapper d-flex {message.author.me
        ? 'justify-content-end'
        : 'justify-content-start'} mb-1 w-100"
    >
      {#if !message.author.me && options.showAvatar}
        <div class="author-name-spacer me-2"></div>
      {/if}
      <div class="author-name-content flex-grow-1">
        <span
          class="fw-semibold {message.author.me
            ? 'text-secondary'
            : 'text-primary'}"
        >
          {message.author.me ? "You" : message.author.name}
        </span>
      </div>
      {#if message.author.me && options.showAvatar}
        <div class="author-name-spacer ms-2"></div>
      {/if}
    </div>
  {/if}

  <div
    class="message-wrapper d-flex {message.author.me
      ? 'justify-content-end'
      : 'justify-content-start'} mb-3 w-100"
  >
    {#if !message.author.me && options.showAvatar}
      <div class="message-avatar me-2">
        {#if message.showAvatar}
          <div class="avatar-container position-relative">
            <img
              src={message.author.avatar ||
                getDefaultAvatar(message.author.isAI)}
              alt={message.author.name}
              class="rounded-circle"
              width="40"
              height="40"
            />
            {#if message.author.isAI}
              <span
                class="badge bg-primary position-absolute top-0 end-0 translate-middle rounded-pill"
              >
                AI
              </span>
            {/if}
          </div>
        {:else}
          <div class="avatar-spacer"></div>
        {/if}
      </div>
    {/if}

    <div class="message-content position-relative">
      {#if options.bubbles}
        <div
          part="message-bubble-{message.author.me ? 'me' : 'them'}"
          class="message-bubble {message.author.me
            ? 'bg-primary text-white'
            : 'bg-light border'} rounded-3 p-3 shadow-sm"
        >
          <div part="message-body" class="message-body">
            {#if message.type === "code"}
              <div class="code-block">
                {#if message.language}
                  <div
                    class="code-header d-flex justify-content-between align-items-center mb-2"
                  >
                    <span class="badge bg-secondary">{message.language}</span>
                  </div>
                {/if}
                <pre class="bg-dark text-light p-3 rounded-2 mb-0"><code
                    >{message.text}</code
                  ></pre>
              </div>
            {:else}
              <div class="message-text">{message.text}</div>
            {/if}
          </div>
        </div>
      {:else}
        <div
          part="message"
          class="message-bubble bg-secondary-subtle border rounded-3 p-3 shadow-sm"
        >
          <div part="message-body" class="message-body">
            {#if message.type === "code"}
              <div class="code-block">
                {#if message.language}
                  <div
                    class="code-header d-flex justify-content-between align-items-center mb-2"
                  >
                    <span class="badge bg-secondary">{message.language}</span>
                  </div>
                {/if}
                <pre class="bg-dark text-light p-3 rounded-2 mb-0"><code
                    >{message.text}</code
                  ></pre>
              </div>
            {:else}
              <div class="message-text">{message.text}</div>
            {/if}
          </div>
        </div>
      {/if}

      <!-- Action buttons -->
      {#if actions?.length}
        <div
          class="message-actions d-flex gap-1 {message.author.me
            ? 'justify-content-end'
            : 'justify-content-start'} mt-2"
        >
          {#each actions as action}
            <button
              class="btn btn-sm btn-outline-secondary rounded-circle p-1"
              onclick={() =>
                dispatch("action", {
                  messageId: message.id,
                  action: action.name,
                })}
              title={action.name}
              aria-label={action.name}
            >
              <i class="bi bi-{action.icon}"></i>
            </button>
          {/each}

          {#if options.showTimestamp}
            <div part="message-timestamp" class="message-timestamp ms-2">
              {message.dateString}
            </div>
          {/if}
        </div>
      {:else if options.showTimestamp}
        <div
          class="message-timestamp-only d-flex {message.author.me
            ? 'justify-content-end'
            : 'justify-content-start'} mt-1"
        >
          <div part="message-timestamp" class="message-timestamp">
            {message.dateString}
          </div>
        </div>
      {/if}
    </div>

    {#if message.author.me && options.showAvatar}
      <div class="message-avatar ms-2">
        {#if message.showAvatar}
          <img
            src={message.author.avatar || getDefaultAvatar(false)}
            alt="You"
            class="rounded-circle"
            width="40"
            height="40"
          />
        {:else}
          <div class="avatar-spacer"></div>
        {/if}
      </div>
    {/if}
  </div>
{/each}

<style lang="scss">
  @forward "./styles/bootstrap.scss";
  @forward "./styles/webcomponent.scss";
</style>
